import {Component, OnInit} from "@angular/core";
import {Preregistration} from "../../../shared/models/preregistration";
import {PreregistrationService} from "../../../shared/services/preregistration/preregistration.service";
import {debounceTime, take} from "rxjs/operators";
import {FormControl} from "@angular/forms";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {PreregistrationModalComponent} from "../preregistration-modal/preregistration-modal.component";
import {PreregistrationImportModalComponent} from "../preregistration-import-modal/preregistration-import-modal.component";
import {PreregistrationDeleteConfirmationModalComponent} from "../preregistration-delete-confirmation-modal/preregistration-delete-confirmation-modal.component";
import {environment} from "../../../../environments/environment";
import {SelectModalComponent} from "../../select-modal/select-modal.component";
import {UserService} from "../../../shared/services/user/user.service";
import {User} from "../../../shared/models/user";
import {ExamService} from "../../../shared/services/exam/exam.service";
import {Exam} from "../../../shared/models/exam";
import {ShortExamSession} from "../../../shared/models/examSession";
import { ExamSessionService } from "src/app/shared/services/exam-session/exam-session.service";
import { ActivatedRoute } from "@angular/router"; // Import the ActivatedRoute service
import {ToastrService} from "ngx-toastr";
import {PreregistrationFilterEnum} from "../../../shared/enums/preregistrationFilterEnum";


@Component({
    "selector": "app-preregistration-list",
    "templateUrl": "./preregistration-list.component.html",
    "styleUrls": ["./preregistration-list.component.scss"]
})
export class PreregistrationListComponent implements OnInit {

    // List of all the exams fetched from backend
    preregistrations: Array<Preregistration>;

    searchTermControl: FormControl;

    sessionFilterControl: FormControl;

    statusFilterControl: FormControl;

    statusCounts: {total: number, waiting: number, to_send: number, to_follow_up: number, confirmed: number, expired: number} = {
        total: 0,
        waiting: 0,
        to_send: 0,
        to_follow_up: 0,
        confirmed: 0,
        expired: 0,
    };

    sessions: Array<ShortExamSession>;

    baseUrl = environment.baseUrl;

    user: User;

    loading: boolean;

    // Pagination related
    page: number;

    pageSize: number;

    itemsCount: number;

    constructor (
        private preregistrationService: PreregistrationService,
        private examService: ExamService,
        private userService: UserService,
        private modalService: NgbModal,
        private sessionService: ExamSessionService,
        private route: ActivatedRoute, // Inject ActivatedRoute service
        private toastr: ToastrService

    ) {

        this.loading = false;
        this.page = 1;
        this.pageSize = environment.pageSize;
        this.searchTermControl = new FormControl("");
        this.sessionFilterControl = new FormControl("all");
        this.statusFilterControl = new FormControl(PreregistrationFilterEnum.ALL);

    }

    ngOnInit () {

        // Filtre de session passé dans l'URL. Lu avant les abonnements aux
        // valueChanges pour que le setValue ne déclenche pas de requêtes en double.
        this.route.queryParams.pipe(take(1)).subscribe(params => {
            const sessionId = params['session_id'];
            if (sessionId) {
                this.sessionFilterControl.setValue(sessionId);
            }
        });

        // Charger les sessions
        this.sessionService.shortList().pipe(take(1))
            .subscribe((sessions: Array<ShortExamSession>) => {
                this.sessions = sessions;
            });

        // Subscribe to the change of value of the search field
        this.searchTermControl.valueChanges.pipe(debounceTime(1000)).
            subscribe(() => {
                this.page = 1;
                this.refresh();
            });

        // Subscribe to the change of value of the session filter
        this.sessionFilterControl.valueChanges.
            subscribe(() => {
                this.page = 1;
                this.refresh();
            });

        // Subscribe to the change of value of the status filter
        this.statusFilterControl.valueChanges.
            subscribe(() => {
                this.page = 1;
                this.refresh();
            });

        this.refresh();

        this.userService.getMe().pipe(take(1)).
            subscribe((user: User) => {
                this.user = user;
            });
    }

    // Une action peut changer le statut d'une ligne et la faire sortir du filtre
    // actif : la page, le total et les compteurs sont donc rechargés ensemble.
    refresh () {
        this.fetchStatusCounts();
        this.fetchItemCount();
        this.fetchCurrentPage();
    }

    setStatusFilter (filter: string) {
        this.statusFilterControl.setValue(filter);
    }

    fetchStatusCounts () {
        const searchTerm = this.searchTermControl.value;
        const sessionFilter = this.sessionFilterControl.value;

        this.preregistrationService.countByStatus(
            searchTerm,
            sessionFilter != "all" ? sessionFilter : null,
        ).pipe(take(1)).
            subscribe((counts) => {
                this.statusCounts = counts;
            });
    }

    fetchCurrentPage () {
        this.loading = true;
        const searchTerm = this.searchTermControl.value;
        const sessionFilter = this.sessionFilterControl.value;

        this.preregistrationService.list(
            this.page,
            searchTerm,
            sessionFilter != "all" ? sessionFilter : null,
            this.statusFilterControl.value,
        ).pipe(take(1)).
            subscribe((res: Array<Preregistration>) => {
                this.preregistrations = res;
                this.loading = false;
            });
    }

    fetchItemCount () {
        const searchTerm = this.searchTermControl.value;
        const sessionFilter = this.sessionFilterControl.value;

        this.preregistrationService.count(
            searchTerm,
            sessionFilter != "all" ? sessionFilter : null,
            this.statusFilterControl.value,
        ).pipe(take(1)).
            subscribe((count: number) => {
                this.itemsCount = count;
            });
    }

    onPageChange (newPage: number) {
        this.page = newPage;
        this.fetchCurrentPage();
    }

    add () {
        const modalRef = this.modalService.open(
            PreregistrationModalComponent,
            {"size": "lg"}
        );

        modalRef.result
            .then((res: Array<Preregistration>) => {
                this.refresh();
            }).catch(() => {
            // Dummy catch to prevent error on close
            });
    }

    openImportModal () {
        const modalRef = this.modalService.open(
            PreregistrationImportModalComponent,
            {"size": "xl", "backdrop": "static", "keyboard": false}
        );

        // Actualise la liste à la fermeture, même si la modale est annulée
        const refresh = () => this.refresh();
        modalRef.result.then(refresh, refresh);
    }

    openSelectExamModal (preregistration: Preregistration) {
        this.examService.list(null, null, null, preregistration.session.id).pipe(take(1)).
            subscribe((exams: Array<Exam>) => {
                const modalRef = this.modalService.open(
                    SelectModalComponent,
                    {"size": "lg"}
                );
                modalRef.componentInstance.title = "Sélectionnez un ticket";
                modalRef.componentInstance.options = exams.filter((exam: Exam) => {
                    return exam.preregistration == null;
                });
                modalRef.componentInstance.defaultOption = preregistration.exam;
                modalRef.result.then((exam: Exam) => {
                    if (exam == null) {
                        this.toastr.error(
                            "Veuillez sélectionner un ticket",
                            "Aucun ticket sélectionné",
                        );
                    }

                    this.preregistrationService.partialUpdate(
                        preregistration.id,
                        {"exam_id": exam.id}
                    ).pipe(take(1)).subscribe(
                        () => {
                            this.refresh();

                            this.toastr.success(
                                "Ticket associé",
                            );
                        },
                        () => {
                            this.toastr.error(
                                "Erreur lors de l'association du ticket",
                                "Erreur",
                            );
                        }
                    );
                    

                }).catch(() => {
                // Dummy catch to prevent error on close
                });
            });
    }

    edit (preregistration: Preregistration) {
        const modalRef = this.modalService.open(
            PreregistrationModalComponent,
            {size: "lg"}
        );
        modalRef.componentInstance.preregistration = preregistration;

        modalRef.result.then((preregistration: Preregistration) => {
            this.refresh();
        }).catch(() => {
            // Dummy catch to avoid ugly errors in console
        });
    }

    remove(preregistration: Preregistration) {
        const modalRef = this.modalService.open(
            PreregistrationDeleteConfirmationModalComponent,
            {size: "lg"}
        );
        modalRef.componentInstance.preregistration = preregistration;

        modalRef.result.then(() => {
            this.refresh();
        }).catch(() => {
            // Dummy catch to prevent error on close
        });
    }

    sendConvocation (preregistration: Preregistration) {
        preregistration.convocationIsSending = true;

        this.preregistrationService.sendConvocation(preregistration.id).pipe(take(1)).
            subscribe(
                () => {
                    preregistration.convocationIsSending = false;
                    this.refresh();
                },
                (error) => {
                    preregistration.convocationIsSending = false;

                    const details = error.error.detail;

                    if (details == null) {
                        this.toastr.error(
                            "Erreur lors de l'envoi de la convocation",
                            "Erreur",
                        );
                        return;
                    } else {
                        this.toastr.error(
                            details,
                            "Erreur",
                        );
                    }
                }
            );
    }
}
